import React from 'react';
import { Search, RefreshCw, Loader2, Keyboard, Star, HelpCircle } from 'lucide-react';

/**
 * EmailToolbar — top bar above the threads list (search + sync + shortcuts).
 *
 * Pure presentational. Owned state lives in `pages/Email.jsx`.
 * Extracted from the monolith (Wave 1u3 refactor step 3).
 */
export default function EmailToolbar({
  searchInput,
  onChangeSearch,
  onSubmitSearch,
  onRefresh,
  loadingList,
  onSyncNow,
  syncing,
  lastSyncLabel,
  onOpenShortcuts,
  onOpenSavedSearches,
  savedSearchesCount = 0,
}) {
  return (
    <div className="p-3 border-b border-[#EFF0F6] flex gap-2" data-testid="email-toolbar">
      <form
        onSubmit={(e) => { e.preventDefault(); onSubmitSearch?.(); }}
        className="flex-1 relative"
      >
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#8E8FA5]" />
        <input
          data-testid="email-search-input"
          value={searchInput}
          onChange={(e) => onChangeSearch?.(e.target.value)}
          placeholder="Rechercher (ex: is:unread from:bob tag:devis has:attachment)"
          className="w-full h-9 pl-8 pr-8 rounded-lg border border-[#EFF0F6] text-[12px] bg-[#F6F7FB] focus:bg-white focus:border-[#C7D2FE]"
        />
        <button
          type="button"
          tabIndex={-1}
          title="Opérateurs disponibles : from:, to:, subject:, has:attachment, is:unread, is:read, is:starred, tag:, assignee:me|none|<id>, before:YYYY-MM-DD, after:YYYY-MM-DD, &quot;phrase exacte&quot;"
          data-testid="email-search-help"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8E8FA5] hover:text-[#4F46E5]"
        >
          <HelpCircle className="w-3.5 h-3.5" />
        </button>
      </form>
      <button
        onClick={onRefresh}
        disabled={loadingList}
        data-testid="email-refresh-btn"
        title="Actualiser (r)"
        className="h-9 w-9 rounded-lg bg-white border border-[#EFF0F6] flex items-center justify-center text-[#6B6C85] hover:bg-[#FAFAFE] disabled:opacity-50"
      >
        {loadingList ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
      </button>
      <button
        onClick={onSyncNow}
        disabled={syncing}
        data-testid="email-sync-now"
        title={lastSyncLabel ? `Synchroniser (dernière sync ${lastSyncLabel})` : 'Synchroniser maintenant'}
        className="h-9 px-2.5 rounded-lg bg-white border border-[#EFF0F6] flex items-center gap-1 text-[#6B6C85] hover:bg-[#FAFAFE] disabled:opacity-50"
      >
        {syncing ? (
          <>
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            <span className="text-[11px] font-semibold hidden lg:inline">Sync…</span>
          </>
        ) : (
          <>
            <RefreshCw className="w-3.5 h-3.5" />
            <span className="text-[11px] font-semibold hidden lg:inline">
              {lastSyncLabel ? `Sync ${lastSyncLabel}` : 'Sync'}
            </span>
          </>
        )}
      </button>
      <button
        onClick={onOpenShortcuts}
        data-testid="email-shortcuts-btn"
        title="Raccourcis clavier (?)"
        className="h-9 w-9 rounded-lg bg-white border border-[#EFF0F6] flex items-center justify-center text-[#6B6C85] hover:bg-[#FAFAFE]"
      >
        <Keyboard className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={onOpenSavedSearches}
        data-testid="email-saved-searches-btn"
        title="Mes filtres sauvegardés"
        className="h-9 w-9 rounded-lg bg-white border border-[#EFF0F6] flex items-center justify-center text-[#6B6C85] hover:bg-[#FAFAFE] relative"
      >
        <Star className="w-3.5 h-3.5" />
        {savedSearchesCount > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#4F46E5] text-white text-[9px] font-bold flex items-center justify-center">
            {savedSearchesCount}
          </span>
        )}
      </button>
    </div>
  );
}
